/**
 * Notification type → destination URL mapping.
 *
 * Extracted from `NotificationCenter.js` so the component stays focused on
 * rendering/state and the routing table can be updated independently.
 */

// Destinations that do not depend on job_id.
const STATIC_ROUTES = {
  // Reviews
  review_received: '/reviews',
  new_review: '/reviews',
  review_visible: '/reviews',

  // Verification
  verification_pending: '/profile?section=verification',
  verification_submitted: '/profile?section=verification',
  verification_approved: '/profile?section=verification',
  verification_rejected: '/profile?section=verification',
  verification_failed: '/profile?section=verification',

  // Account
  profile_update: '/profile',
  settings_changed: '/profile',
  earnings_milestone: '/my-earnings',
  payout_processed: '/my-earnings',
  payout_arrived: '/my-earnings',
  bank_payout_initiated: '/my-earnings',
  bank_payout_failed: '/profile?step=hero&subtab=payouts',
  payout_recovered: '/my-earnings',
  referral_reward: '/referrals',
  referral_signup: '/referrals',

  // Messages
  new_message: '/messages',
  message_received: '/messages',

  // Admin
  admin_message: '/dashboard',
  system_announcement: '/dashboard',
  account_suspended: '/dashboard',
  account_warning: '/dashboard',

  // Money
  refund_approved: '/my-earnings',
  refund_completed: '/my-earnings',
  refund_processed: '/my-earnings',
  money_request_approved: '/my-earnings',
  money_received: '/my-earnings',
  money_request_rejected: '/messages',
  money_request_response: '/messages',
  payment_sent: '/my-earnings',
  money_sent: '/my-earnings',

  // Jobs
  bid_rejected: '/dashboard?tab=hero',
  application_rejected: '/dashboard?tab=hero',
  job_confirmed: '/my-earnings',
  payment_confirmed: '/my-earnings',

  // No-show / suspension
  bidding_suspended: '/browse-jobs',
  appeal_approved: '/browse-jobs',
  appeal_rejected: '/browse-jobs',
  bidding_restored: '/browse-jobs',
  posting_suspended: '/dashboard',
  posting_restored: '/dashboard',
  client_no_show_reported: '/dashboard',
  client_no_show_confirmed: '/dashboard',
  // Scheduler catch-up digest (older jobs closed) → client's job list
  catchup_digest: '/dashboard?tab=client',
};

// Destinations that fall back to `/dashboard` when job_id is absent.
const JOB_OR_DASHBOARD = new Set([
  'payment_received', 'payment_successful',
  'job_completed', 'pending_confirmation',
  'job_reminder',
  'refund_request', 'refund_requested', 'refund_pending',
  'refund_rejected',
  'money_request', 'payment_request',
  'review_submitted_waiting', 'review_reminder',
  'booking_reminder',
  'schedule_changed',
]);

// Destinations that fall back to hero dashboard.
const JOB_OR_HERO_DASHBOARD = new Set([
  'bid_accepted', 'application_accepted',
]);

// Destinations that fall back to client dashboard.
const JOB_OR_CLIENT_DASHBOARD = new Set([
  'new_bid', 'new_application',
  'payment_required', 'pending_payment',
]);

// Live-tracking destinations.
const TRACKING_TYPES = new Set(['job_started', 'hero_en_route', 'hero_on_way']);

// Job-feed destinations.
const BROWSE_JOBS_TYPES = new Set(['new_job', 'matching_job', 'job_alert']);


/**
 * Resolve the URL a notification should navigate to when clicked.
 *
 * @param {string} notifType
 * @param {object} data - `notification.data` payload, may contain `job_id`,
 *                       `conversation_id`, `action_url`.
 * @returns {string} target path
 */
export function getNotificationRoute(notifType, data = {}) {
  const jobId = data.job_id;

  if (BROWSE_JOBS_TYPES.has(notifType)) {
    return data.action_url || '/browse-jobs';
  }

  if (notifType === 'interview_started') {
    if (data.conversation_id) return `/messages?conversation=${data.conversation_id}`;
    if (jobId) return `/messages?job=${jobId}`;
    return '/messages';
  }

  if (TRACKING_TYPES.has(notifType)) {
    return jobId ? `/tracking/${jobId}` : '/dashboard?tab=client';
  }

  if (STATIC_ROUTES[notifType]) {
    return STATIC_ROUTES[notifType];
  }

  if (JOB_OR_HERO_DASHBOARD.has(notifType)) {
    return jobId ? `/jobs/${jobId}` : '/dashboard?tab=hero';
  }

  if (JOB_OR_CLIENT_DASHBOARD.has(notifType)) {
    return jobId ? `/jobs/${jobId}` : '/dashboard?tab=client';
  }

  if (JOB_OR_DASHBOARD.has(notifType)) {
    return jobId ? `/jobs/${jobId}` : '/dashboard';
  }

  // Default: go to job if we have one, otherwise dashboard.
  return jobId ? `/jobs/${jobId}` : '/dashboard';
}
